// angular imports
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';
import notify from 'devextreme/ui/notify';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// constant imports
import { CacheDuration } from '../../_constants/cache-duration';
import { MessageInterval } from '../../_constants/messages-intervals';
import { URLConstants } from '../../_constants/url-constants';

// service imports
import { EmailSharedService } from '../../_services/shared-services/email-shared.service';
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';

// model imports
import { KeyValueResultDto } from '../../_models/key-value-result-dto';
import { StatusMessageResultDto } from '../../_models/status-message-result-dto';
import { EmailContentAttachmentParameterDto } from '../../ciramail-module/_models/email-composer/email-content-attachment-parameter-dto';
import { CiramailActionItemsParameterDto } from '../_models/cira-mail-action-items-parameter-dto';
import { CiramailActionsParameterDto } from '../_models/cira-mail-actions-parameter-dto';
import { CiramailAssignToTeamResultDto } from '../_models/cira-mail-assign-to-team-result-dto';
import { CiramailAssignToUsersParameterDto } from '../_models/cira-mail-assign-to-users-parameter-dto';
import { CiramailExportParameterDto } from '../_models/cira-mail-export-parameter-dto';
import { CiramailMoveToListParameterDto } from '../_models/cira-mail-move-to-list-parameter-dto';
import { CiramailTransferListParameterDto } from '../_models/cira-mail-transfer-list-parameter-dto';
import { CiramailTransferParameterDto } from '../_models/cira-mail-transfer-parameter-dto';
import { CiramailWorkQueueParameterDto } from '../_models/cira-mail-work-queue-parameter-dto';
import { CiramailWorkflowParameterDto } from '../_models/cira-mail-workflow-parameter-dto';
import { EmailAttachmentVendorSelectorParameterDto } from '../_models/email-attachment-vendor-selector-parameter-dto';
import { EmailAttachmentVendorSelectorResultDto } from '../_models/email-attachment-vendor-selector-result-dto';
import { EmailAttributionRequirementParameterDto } from '../_models/email-attribution-requirement-parameter-dto';
import { EmailContentPlainTextParameterDto } from '../_models/email-content-plain-text-parameter-dto';
import { EmailContentPlainTextResultDto } from '../_models/email-content-plain-text-result-dto';
import { UploadDocumentParameterDto } from '../_models/upload-document-parameter-dto';
import { UploadEmailDocumentEditorResultDto } from '../_models/upload-email-document-editor-result-dto';
import { UploadEmailDocumentParameterDto } from '../_models/upload-email-document-parameter-dto';
import { ValidateFileNameExistParameterDto } from '../_models/validate-file-name-exist-parameter-dto';
import { ValidateFileNameExistResultDto } from '../_models/validatefile-name-exist-result-dto';

@Injectable({
    providedIn: 'root'
})
export class EmailActionsService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService,
        private readonly emailSharedService: EmailSharedService,
        private readonly errorService: ServerErrorService
    ) {
        this.emailSharedService.onUpdateFolder().subscribe((params) => {
            this.moveEmailTo(
                params.selectedEmailCacheIds,
                params.sourceFolder,
                params.targetFolder,
                params.emailAccount
            ).subscribe(
                (result: StatusMessageResultDto) => {
                    if (result.isSuccessMessage) {
                        notify(result.message, 'Success', MessageInterval.successMsgInterval);
                        this.emailSharedService.onForceRepaint();
                    } else {
                        this.errorService.updateSubjectErrorMessage(result.message);
                    }
                },
                (error) => {
                    this.errorService.displayError(error);
                }
            );
        });
    }

    getTransferList(baseParams: CiramailTransferListParameterDto): Observable<KeyValueResultDto[]> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/transferlist${baseParams.getQueryParams()}`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData: KeyValueResultDto[]) => plainToClass(KeyValueResultDto, responseData)));
    }

    transferEmail(baseParams: CiramailTransferParameterDto): Observable<StatusMessageResultDto> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/transfer`;
        return this.httpClient
            .post(url, baseParams)
            .pipe(map((responseData: any) => plainToClass(StatusMessageResultDto, responseData)));
    }

    getStatusList(baseParams: EmailAttributionRequirementParameterDto): Observable<string[]> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/statuslist${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient.get(url, options).pipe(map((responseData: string[]) => responseData));
    }

    getMoveToList(baseParams: CiramailMoveToListParameterDto): Observable<string[]> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/movetolist${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient.get(url, options).pipe(map((responseData: string[]) => responseData));
    }

    getPriorityList(): Observable<KeyValueResultDto[]> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/prioritylist`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData: KeyValueResultDto[]) => plainToClass(KeyValueResultDto, responseData)));
    }

    getAssignedToList(baseParams: CiramailAssignToUsersParameterDto): Observable<CiramailAssignToTeamResultDto[]> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/assigntolist`;
        return this.httpClient
            .post(url, baseParams)
            .pipe(
                map((responseData: CiramailAssignToTeamResultDto[]) =>
                    plainToClass(CiramailAssignToTeamResultDto, responseData)
                )
            );
    }

    createWorkflow(baseParams: CiramailWorkflowParameterDto): Observable<StatusMessageResultDto> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/workflow`;
        return this.httpClient
            .post(url, baseParams)
            .pipe(map((responseData: any) => plainToClass(StatusMessageResultDto, responseData)));
    }

    createWorkQueue(baseParams: CiramailWorkQueueParameterDto): Observable<StatusMessageResultDto> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/workqueue`;
        return this.httpClient
            .post(url, baseParams)
            .pipe(map((responseData: any) => plainToClass(StatusMessageResultDto, responseData)));
    }

    exportEmail(baseParams: CiramailExportParameterDto): Observable<Blob> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/export${baseParams.getQueryParams()}`;

        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    customEmailActions(baseParams: CiramailActionsParameterDto): Observable<StatusMessageResultDto> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/actions`;
        return this.httpClient
            .post(url, baseParams)
            .pipe(map((responseData: StatusMessageResultDto) => plainToClass(StatusMessageResultDto, responseData)));
    }

    downloadFile(fileUrl: string): Observable<Blob> {
        return this.httpClient
            .get(fileUrl, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    moveEmailTo(
        selectedEmailCacheIds: Array<number>,
        currentFolder: string,
        targetFolder: string,
        emailAccount: string
    ): Observable<StatusMessageResultDto> {
        const editedItems: Array<CiramailActionItemsParameterDto> = [];
        selectedEmailCacheIds.forEach((cacheId) => {
            editedItems.push({
                emailInfoCacheId: cacheId,
                action: 'moveto',
                value: targetFolder,
                currentFolder
            });
        });
        const baseParam: CiramailActionsParameterDto = new CiramailActionsParameterDto();
        baseParam.emailAccount = emailAccount;
        baseParam.items = editedItems;
        return this.customEmailActions(baseParam);
    }

    getEmailAttachment(params: EmailContentAttachmentParameterDto): Observable<Blob> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/content/attachment?${params.getQueryParams()}`;
        this.cacheRegistrationService.registerForCache(url, params.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        return this.httpClient
            .get(url, { headers, responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    getUploadOwnerDocumentEditor(
        baseParams: UploadEmailDocumentParameterDto
    ): Observable<UploadEmailDocumentEditorResultDto> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/uploaddocument/owner${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData: any) => plainToClass(UploadEmailDocumentEditorResultDto, responseData)));
    }

    getUploadCommunityDocumentEditor(
        baseParams: UploadEmailDocumentParameterDto
    ): Observable<UploadEmailDocumentEditorResultDto> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/uploaddocument/community${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData: any) => plainToClass(UploadEmailDocumentEditorResultDto, responseData)));
    }

    validateUploadDocument(
        baseParams: ValidateFileNameExistParameterDto
    ): Observable<ValidateFileNameExistResultDto[]> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/uploaddocument/validate`;
        return this.httpClient
            .post(url, classToPlain(baseParams))
            .pipe(map((responseData: any[]) => plainToClass(ValidateFileNameExistResultDto, responseData)));
    }

    uploadOwnerDocument(baseParams: UploadDocumentParameterDto): Observable<any> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/uploaddocument/owner`;
        return this.httpClient.post(url, classToPlain(baseParams));
    }

    uploadCommunityDocument(baseParams: UploadDocumentParameterDto): Observable<any> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/uploaddocument/community`;
        return this.httpClient.post(url, classToPlain(baseParams));
    }

    getContentPlainText(baseParams: EmailContentPlainTextParameterDto): Observable<EmailContentPlainTextResultDto> {
        const url = `${URLConstants.ccCiramailBaseApiUrl}/content/plaintext${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((responseData: any) => plainToClass(EmailContentPlainTextResultDto, responseData)));
    }

    getEmailAttachmentVendorList(
        params: EmailAttachmentVendorSelectorParameterDto
    ): Observable<EmailAttachmentVendorSelectorResultDto[]> {
        const url = `${URLConstants.ccCommunityVendorBaseApiUrl}/selector${params.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData: any[]) => plainToClass(EmailAttachmentVendorSelectorResultDto, responseData)));
    }
}
